/**
 * Composable for Company mission creation wizard.
 * Handles business logic specific to company/organization mode (is_organization === true).
 *
 * Key differences from Freelance mode:
 * - Does NOT send business_provider (no commissions generated)
 * - 6 steps total (3 visible in stepper)
 * - Uses BPublishMissionStep1-6 components
 * - Includes expert search flow after mission creation
 */

import { ref, computed, nextTick } from 'vue';
import { useMissionStoreV1 } from '~/stores/v1/missionStoreV1';
import { missionStore as useMissionStore } from '~/stores/mission';
import { useInvitationStore } from '~/stores/invitation';
import { useCompanyStore } from '~/stores/companyStore';
import { useAlerts } from '~/stores/alert';
import { authStore } from '~/stores/auth';
import type { Mission, CreateMissionPayload, UpdateMissionPayload } from '~/api/v2/missions/MissionRepository';
import { useTracking } from '~/composables/useTracking';
import { isNonDiffusible } from '~/help/nonDiffusible';
import { DEFAULT_LANGUAGES } from '~/help/languages';

export interface SelectedExpert {
  value: string;
  label: string;
  picture_url?: string;
}

export interface CompanyWizardConfig {
  onMissionCreated?: () => void | Promise<void>;
  onClose?: () => void;
}

export const useCompanyMissionWizard = (config: CompanyWizardConfig = {}) => {
  const { t } = useI18n();
  const { trackEvent } = useTracking();
  const missionStore = useMissionStoreV1();
  const storeMission = useMissionStore();
  const { invitationInvite } = useInvitationStore();
  const companyStore = useCompanyStore();
  const alerts = useAlerts();

  // State
  const currentStep = ref(0);
  const loading = ref(false);
  const createdMission = ref<Mission | null>(null);
  const selectedExperts = ref<SelectedExpert[]>([]);
  const showSaveDraftModal = ref(false);
  const isRestoringDraft = ref(false);
  const action = ref<'create' | 'update'>('create');

  // Step data (collected from step components)
  const stepData = ref({
    step1: {} as Record<string, any>,
    step2: {} as Record<string, any>,
    step3: {} as Record<string, any>,
  });

  // Constants
  const TOTAL_STEPS = 6;
  const TOTAL_VISIBLE_STEPS = 3;
  const STEPPER_VISIBLE_STEPS = [0, 1, 2];
  const SHOW_BACK_BUTTON_STEPS = [1, 2];
  const HIDE_HEADER_STEPS = [5]; // Success step
  const HIDE_FOOTER_STEPS = [5]; // Success step has its own CTA

  // Step titles
  const stepTitles = computed(() => [
    t('publish_mission.step1.title'),   // 0: Marché client adressé
    t('publish_mission.step2.title'),   // 1: Profil recherché
    t('publish_mission.step3.title'),   // 2: Conditions du projet
    t('publish_mission.step4.title'),   // 3: C'est fait!
    t('publish_mission.step5.title'),   // 4: Trouver un expert
    t('publish_mission.step6.title'),   // 5: Mission publiée!
  ]);

  const currentTitle = computed(() => t('publish_mission.modal_title'));
  const currentDescription = computed(() => stepTitles.value[currentStep.value] || '');

  // True when the user's own company still carries the "[NON DIFFUSIBLE]" marker
  // (registered before the fix). They must clean up their company identity before
  // creating anything, so we block them right on step 1 (see BPublishMissionStep1's
  // banner). Only relevant in "own company" mode — external client companies are
  // guarded server-side.
  const ownCompanyNonDiffusible = computed(() =>
    isNonDiffusible(companyStore.company?.business_name) || isNonDiffusible(companyStore.company?.address)
  );

  // Validation
  const canGoNext = computed(() => {
    if (currentStep.value === 0) {
      const data = stepData.value.step1;
      // Block advancing when the user targets their own company but it is still
      // "[NON DIFFUSIBLE]" — they must fix its name/address first.
      const usingOwnCompany = data.use_own_company ?? true;
      if (usingOwnCompany && ownCompanyNonDiffusible.value) {
        return false;
      }
      const hasPersonas = Array.isArray(data.personas) && data.personas.length > 0;
      const hasDealSizes = Array.isArray(data.deal_sizes) && data.deal_sizes.length > 0;
      const hasMarketSegments = Array.isArray(data.market_segments) && data.market_segments.length > 0;
      return Boolean(data.company_type && data.industry && hasPersonas && hasDealSizes && hasMarketSegments);
    }
    if (currentStep.value === 1) {
      const data = stepData.value.step2;
      return Boolean(data.job_position?.trim() && Array.isArray(data.skills) && data.skills.length > 0 && data.description?.trim());
    }
    if (currentStep.value === 2) {
      // Default is pre-checked but not yet emitted until the user interacts with
      // the chip group; fall back to DEFAULT_LANGUAGES so step 3 isn't wrongly
      // blocked before the first change event.
      const languages = stepData.value.step3.languages ?? DEFAULT_LANGUAGES;
      return Array.isArray(languages) && languages.length > 0;
    }
    return true;
  });

  // Button text
  const nextButtonText = computed(() => {
    switch (currentStep.value) {
      case 0:
      case 1:
        return t('common.next');
      case 2:
        return t('publish_mission.publish_mission');
      case 3:
        return t('publish_mission.find_expert_now');
      case 4:
        return t('publish_mission.send_to_experts');
      case 5:
        return t('publish_mission.go_to_missions');
      default:
        return t('common.next');
    }
  });

  // Step handlers
  const onStep1Change = (data: Record<string, any>) => {
    stepData.value.step1 = data;
  };

  const onStep2Change = (data: Record<string, any>) => {
    stepData.value.step2 = data;
  };

  const onStep3Change = (data: Record<string, any>) => {
    stepData.value.step3 = data;
  };

  // Main navigation handler
  const onNext = async (): Promise<boolean> => {
    if (loading.value) return false;

    loading.value = true;
    try {
      switch (currentStep.value) {
        case 0:
        case 1:
          currentStep.value++;
          return true;

        case 2: {
          // Create or update mission WITHOUT business_provider (company mode - no commissions)
          const step1 = stepData.value.step1;
          const step2 = stepData.value.step2;
          const step3 = stepData.value.step3;

          // Get SIREN: undefined when own company (backend uses company_id directly), from step1 when external
          const isOwnCompany = step1.use_own_company ?? true;
          const siren = isOwnCompany ? undefined : step1.siren;

          const payload: CreateMissionPayload = {
            name: step2.job_position,
            description: step2.description || undefined,
            siren: siren || undefined,
            contact_email: step1.contact_email || undefined,
            budget: step3.budget ? Number(step3.budget) : undefined,
            is_public: step3.is_public ?? true,
            // TEMPORARILY HIDDEN: mobility_type: step3.work_location || undefined,
            company_size: step1.company_type || undefined,
            industry: step1.industry || undefined,
            personas: step1.personas?.map((p: { value: string }) => p.value) || undefined,
            deal_sizes: step1.deal_sizes || undefined,
            market_segments: step1.market_segments || undefined,
            skills: step2.skills?.map((s: { value: string }) => s.value) || undefined,
            languages: step3.languages || undefined,
            // NOTE: business_provider is NOT sent - this is the key difference from freelance mode
          };

          if (action.value === 'update' && createdMission.value) {
            const updatePayload: UpdateMissionPayload = {
              name: step2.job_position,
              description: step2.description || undefined,
              budget: step3.budget ? Number(step3.budget) : undefined,
              is_public: step3.is_public ?? true,
              skills: step2.skills?.map((s: { value: string }) => s.value) || undefined,
              languages: step3.languages || undefined,
              context: {
                company_size_id: step1.company_type || undefined,
                industry_id: step1.industry || undefined,
                persona_ids: step1.personas?.map((p: { value: string }) => p.value) || undefined,
                deal_size_ids: step1.deal_sizes || undefined,
                market_segment_ids: step1.market_segments || undefined,
              },
            };
            createdMission.value = await storeMission.updateMission(createdMission.value.id, updatePayload) as unknown as Mission;
            await config.onMissionCreated?.();
            currentStep.value++;
            return true;
          }

          // Create new mission (guard against duplicates if retrying after callback error)
          if (!createdMission.value) {
            const companyId = companyStore.company?.company_id;
            let response: Mission;
            try {
              // skipGlobalErrorAlert: surface a translated message for the "[NON DIFFUSIBLE]"
              // guard instead of the raw backend error key.
              response = await missionStore.createMission(payload, companyId, { skipGlobalErrorAlert: true }) as unknown as Mission;
            } catch (createError) {
              const problem = (createError as any)?.data?.data;
              if (problem?.title === 'company_non_diffusible_fields') {
                alerts.add({ type: 'danger', title: t('company.nonDiffusibleBlocked') });
              } else {
                alerts.add({ type: 'danger', title: t('common.error_occurred') });
              }
              return false;
            }
            createdMission.value = response;

            storeMission.clearDraft();
            trackEvent("mission", "mission_created");
          }

          // Trigger mission created callback immediately after creation
          // This ensures the missions list is refreshed even if user closes modal early
          await config.onMissionCreated?.();

          currentStep.value++;
          return true;
        }

        case 3:
          // User chose "Trouver un expert maintenant"
          currentStep.value++;
          return true;

        case 4: {
          // Send invitations to selected experts
          if (createdMission.value && selectedExperts.value.length > 0) {
            const invitationPromises = selectedExperts.value.map((expert) =>
              invitationInvite(createdMission.value!.id, { user_id: expert.value })
            );
            await Promise.all(invitationPromises);
            trackEvent("mission", "expert_invited");

            await config.onMissionCreated?.();
          }
          currentStep.value++;
          return true;
        }

        case 5:
          return true;

        default:
          return false;
      }
    } catch (error) {
      console.error('Error in company wizard step:', error);
      return false;
    } finally {
      loading.value = false;
    }
  };

  // Skip expert search (go directly to success)
  const onSkipExpertSearch = () => {
    currentStep.value = 5;
  };

  // Go back one step
  const goBack = () => {
    if (currentStep.value > 0) {
      currentStep.value--;
    }
  };

  const onFinish = async () => {
  };

  // Draft management
  const saveDraft = () => {
    if (action.value !== 'create') return;

    storeMission.saveDraft({
      currentStep: currentStep.value,
      step1Data: { ...stepData.value.step1 },
      step2Data: { ...stepData.value.step2 },
      step3Data: { ...stepData.value.step3 },
      step4Data: {},
      step5Data: {},
    });
  };

  // Check if there's unsaved data
  const hasUnsavedData = computed(() => {
    return Object.values(stepData.value).some(
      (step) => Object.keys(step).length > 0
    );
  });

  // Handle modal close with draft prompt
  const onClose = () => {
    if (hasUnsavedData.value && currentStep.value < 3) {
      showSaveDraftModal.value = true;
    } else {
      reset();
      config.onClose?.();
    }
  };

  const onSaveDraft = () => {
    saveDraft();
    showSaveDraftModal.value = false;
    config.onClose?.();
  };

  const onDiscardDraft = () => {
    showSaveDraftModal.value = false;
    reset();
    config.onClose?.();
  };

  const saveDraftOnClose = () => {
    if (action.value !== 'create') return;

    // Only save draft if we have unsaved data and haven't completed mission creation (step < 3)
    if (currentStep.value < 3 && hasUnsavedData.value === true) {
      saveDraft();
    }
  };

  const restoreDraftFromStore = () => {
    if (action.value !== 'create') return;

    const draft = storeMission.getDraft;
    // Only restore if draft exists and we're before mission creation
    if (draft && draft.currentStep < 3) {
      isRestoringDraft.value = true;
      currentStep.value = draft.currentStep;
      stepData.value.step1 = { ...draft.step1Data };
      stepData.value.step2 = { ...draft.step2Data };
      stepData.value.step3 = { ...draft.step3Data };
      // Reset flag after next tick to allow children to process
      nextTick(() => {
        isRestoringDraft.value = false;
      });
    }
  };

  // Reset wizard state
  const reset = () => {
    currentStep.value = 0;
    stepData.value = { step1: {}, step2: {}, step3: {} };
    createdMission.value = null;
    selectedExperts.value = [];
    loading.value = false;
    action.value = 'create';
  };

  // Initialize company data when wizard opens
  const initialize = async () => {
    const auth = authStore();
    const companyId = auth.user?.company_id;
    if (companyId) {
      try {
        await companyStore.fetchCompany(companyId);
      } catch (e) {
        console.error('Error fetching company for mission creation:', e);
      }
    }
  };

  if (action.value === 'create') {
    restoreDraftFromStore();
  }

  return {
    // State
    currentStep,
    loading,
    createdMission,
    selectedExperts,
    showSaveDraftModal,
    stepData,
    isRestoringDraft,
    action,

    // Config
    TOTAL_STEPS,
    TOTAL_VISIBLE_STEPS,
    STEPPER_VISIBLE_STEPS,
    SHOW_BACK_BUTTON_STEPS,
    HIDE_HEADER_STEPS,
    HIDE_FOOTER_STEPS,

    // Computed
    currentTitle,
    currentDescription,
    canGoNext,
    nextButtonText,
    hasUnsavedData,

    // Methods
    onStep1Change,
    onStep2Change,
    onStep3Change,
    onNext,
    onSkipExpertSearch,
    goBack,
    onFinish,
    onClose,
    onSaveDraft,
    onDiscardDraft,
    saveDraftOnClose,
    restoreDraftFromStore,
    reset,
    initialize,
  };
};
